import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import api from "@/lib/api";
import { UploadSimple, FloppyDisk } from "@phosphor-icons/react";

const EMPTY = { title: "", slug: "", excerpt: "", body_md: "", cover_image_url: "", tags: [], published: true, author: "Maxine Team" };

export default function InsightEditor() {
  const nav = useNavigate();
  const { id } = useParams();
  const [v, setV] = useState(EMPTY);
  const [preview, setPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!id) return;
    api.get("/insights").then((r) => {
      const found = r.data.find((x) => x.id === id);
      if (found) setV(found);
    });
  }, [id]);

  const set = (k, val) => setV((s) => ({ ...s, [k]: val }));

  const uploadCover = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
      set("cover_image_url", url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
  };

  const save = async () => {
    if (!v.title || !v.slug || !v.body_md) { toast.error("Title, slug and body are required"); return; }
    setLoading(true);
    try {
      const payload = { ...v };
      if (id && v.id) {
        const { id: _id, ...rest } = payload;
        await api.put(`/insights/${_id}`, rest);
      } else {
        await api.post("/insights", payload);
      }
      toast.success("Saved");
      nav("/admin/insights");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setLoading(false); }
  };

  const insertMd = (before, after = before) => {
    const ta = document.getElementById("md-body");
    if (!ta) return;
    const s = ta.selectionStart; const e = ta.selectionEnd;
    const val = v.body_md;
    const newVal = val.slice(0, s) + before + val.slice(s, e) + after + val.slice(e);
    set("body_md", newVal);
  };

  return (
    <div data-testid="insight-editor" className="p-8 md:p-10">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display font-black text-3xl md:text-4xl">{id ? "Edit post" : "New post"}</h1>
        <div className="flex gap-2">
          <button onClick={() => setPreview((p) => !p)} data-testid="editor-preview" className="btn-ghost text-sm py-2.5 px-4">{preview ? "Edit" : "Preview"}</button>
          <button onClick={save} disabled={loading} data-testid="editor-save" className="btn-brand text-sm py-2.5 px-4">
            <FloppyDisk size={14} weight="bold" /> {loading ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-[color:var(--line)] p-6 space-y-4">
          <Field label="Title" value={v.title} onChange={(x) => set("title", x)} testid="editor-title" />
          <Field label="Slug (URL)" value={v.slug} onChange={(x) => set("slug", x.toLowerCase().replace(/\s+/g, "-"))} testid="editor-slug" />
          <Field label="Excerpt (1-2 sentences)" value={v.excerpt} onChange={(x) => set("excerpt", x)} textarea testid="editor-excerpt" />

          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Body (Markdown)</label>
            <div className="mt-2 flex flex-wrap gap-1 border border-[color:var(--line)] border-b-0 bg-neutral-50 px-2 py-1 text-xs">
              {[
                ["B", () => insertMd("**")],
                ["I", () => insertMd("_")],
                ["H2", () => insertMd("\n## ", "\n")],
                ["H3", () => insertMd("\n### ", "\n")],
                ["Link", () => insertMd("[", "](https://)")],
                ["List", () => insertMd("\n- ")],
                ["Code", () => insertMd("`")],
                ["Quote", () => insertMd("\n> ")],
              ].map(([label, fn]) => (
                <button key={label} onClick={fn} data-testid={`md-${label.toLowerCase()}`} type="button" className="px-2 py-1 hover:bg-white border border-transparent hover:border-[color:var(--line)]">{label}</button>
              ))}
            </div>
            {preview ? (
              <div data-testid="md-preview" className="border border-[color:var(--line)] p-4 min-h-[300px] prose-md">
                <ReactMarkdown>{v.body_md || "*Nothing to preview yet*"}</ReactMarkdown>
              </div>
            ) : (
              <textarea id="md-body" data-testid="editor-body"
                value={v.body_md} onChange={(e) => set("body_md", e.target.value)} rows={16}
                className="w-full border border-[color:var(--line)] p-4 outline-none focus:border-[color:var(--brand)] font-mono text-sm leading-relaxed" />
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white border border-[color:var(--line)] p-6">
            <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Cover image</label>
            {v.cover_image_url && <img src={v.cover_image_url} alt="cover" className="mt-3 w-full border border-[color:var(--line)]" />}
            <div className="mt-3 flex flex-col gap-2">
              <input type="file" ref={fileRef} className="hidden" accept="image/*" onChange={uploadCover} data-testid="editor-file" />
              <button type="button" onClick={() => fileRef.current?.click()} className="btn-ghost text-sm py-2 px-3 justify-center">
                <UploadSimple size={14} weight="bold" /> Upload cover
              </button>
              <input value={v.cover_image_url || ""} onChange={(e) => set("cover_image_url", e.target.value)} placeholder="or paste an image URL"
                className="border border-[color:var(--line)] px-3 py-2 text-xs outline-none focus:border-[color:var(--brand)]" data-testid="editor-cover-url" />
            </div>
          </div>

          <div className="bg-white border border-[color:var(--line)] p-6 space-y-4">
            <Field label="Author" value={v.author} onChange={(x) => set("author", x)} testid="editor-author" />
            <Field label="Tags (comma-separated)" value={(v.tags || []).join(", ")}
              onChange={(x) => set("tags", x.split(",").map((s) => s.trim()).filter(Boolean))} testid="editor-tags" />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={v.published} onChange={(e) => set("published", e.target.checked)} data-testid="editor-published" className="accent-[color:var(--brand)]" />
              Published
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, textarea, testid }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{label}</label>
      {textarea ? (
        <textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
          className="mt-1 w-full border border-[color:var(--line)] px-4 py-2.5 outline-none focus:border-[color:var(--brand)]" />
      ) : (
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
          className="mt-1 w-full border border-[color:var(--line)] px-4 py-2.5 outline-none focus:border-[color:var(--brand)]" />
      )}
    </div>
  );
}
